import React, { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import LayersIcon from '@mui/icons-material/Layers';
import HotelIcon from '@mui/icons-material/Hotel';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getConfigurationActionHeaderRowSx,
  getConfigurationActionHeaderTitleSx,
  getConfigurationAddButtonSx,
  getConfigurationCardGridSpacing,
  getConfigurationDrawerFooterSx,
  getConfigurationEmbeddedRootSx,
  isConfigurationMobileLayout,
} from '../utils/configurationMobileLayout';
import { getConfigurationFloors, createFloor, updateFloor, deleteFloor } from '../services/api';

const FloorPage = () => {
  const isMobileLayout = isConfigurationMobileLayout();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogState, setConfirmDialogState] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    floor_number: '',
    name: '',
    description: '',
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    fetchFloors();
  }, []);

  const isDark = currentTheme === 'dark';

  const fetchFloors = async () => {
    setLoading(true);
    try {
      const data = await getConfigurationFloors({ includeInactive: true });
      setFloors(data);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load floors. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (floor = null) => {
    if (floor) {
      // Edit mode
      setEditingId(floor.id);
      setFormData({
        floor_number: floor.floor_number,
        name: floor.name || '',
        description: floor.description || '',
        is_active: floor.is_active,
      });
    } else {
      // Create mode
      setEditingId(null);
      setFormData({
        floor_number: '',
        name: '',
        description: '',
        is_active: true,
      });
    }
    setErrors({});
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditingId(null);
    setFormData({
      floor_number: '',
      name: '',
      description: '',
      is_active: true,
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSaveFloor = async () => {
    setErrors({});
    setSaving(true);

    try {
      let response;
      if (editingId) {
        // Update existing floor
        response = await updateFloor(editingId, formData);
        setSnackbar({
          open: true,
          message: response.message || 'Floor updated successfully!',
          severity: 'success',
        });
      } else {
        // Create new floor
        response = await createFloor(formData);
        setSnackbar({
          open: true,
          message: response.message || 'Floor created successfully!',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchFloors();
    } catch (error) {if (error.errors) {
        setErrors(error.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.error || `Failed to ${editingId ? 'update' : 'create'} floor. Please try again.`,
          severity: 'error',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleServiceable = async (floor) => {
    setConfirmDialogState({ action: floor.is_active ? 'disable' : 'enable', floor });
    setConfirmInput('');
    setConfirmDialogOpen(true);
  };

  const handleDeleteClick = (floor) => {
    setConfirmDialogState({ action: 'delete', floor });
    setConfirmInput('');
    setConfirmDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    if (deletingId !== null || togglingId !== null) return;
    setConfirmDialogOpen(false);
    setConfirmDialogState(null);
    setConfirmInput('');
  };

  const handleConfirmAction = async () => {
    const targetFloor = confirmDialogState?.floor;
    if (!targetFloor) return;
    const isDisableOrDeleteAction = ['disable', 'delete'].includes(confirmDialogState?.action);
    if (isDisableOrDeleteAction && targetFloor?.has_active_bookings) {
      setSnackbar({
        open: true,
        message:
          targetFloor?.active_booking_alert ||
          'Cannot proceed because active bookings exist for this floor.',
        severity: 'warning',
      });
      return;
    }

    if (confirmDialogState.action === 'delete') {
      setDeletingId(targetFloor.id);
      try {
        await deleteFloor(targetFloor.id);
        setSnackbar({
          open: true,
          message: 'Floor deleted successfully!',
          severity: 'success',
        });
        setConfirmDialogOpen(false);
        setConfirmDialogState(null);
        setConfirmInput('');
        fetchFloors();
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.error || error.message || 'Failed to delete floor. Please try again.',
          severity: 'error',
        });
      } finally {
        setDeletingId(null);
      }
      return;
    }

    if (confirmDialogState.action === 'disable' || confirmDialogState.action === 'enable') {
      setTogglingId(targetFloor.id);
      try {
        const isEnableAction = confirmDialogState.action === 'enable';
        await updateFloor(targetFloor.id, { is_active: isEnableAction });
        setSnackbar({
          open: true,
          message: `Floor ${isEnableAction ? 'enabled' : 'disabled'} successfully!`,
          severity: 'success',
        });
        setConfirmDialogOpen(false);
        setConfirmDialogState(null);
        setConfirmInput('');
        fetchFloors();
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to update floor status. Please try again.',
          severity: 'error',
        });
      } finally {
        setTogglingId(null);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getRoomCountText = (count) => {
    return count === 1 ? '1 Room' : `${count} Rooms`;
  };

  const getFloorDisplayName = (floor) => {
    if (floor.name) {
      return floor.name;
    }
    if (floor.floor_number === 0) {
      return 'Ground';
    }
    if (floor.floor_number < 0) {
      return `Basement ${Math.abs(floor.floor_number)}`;
    }
    return `Floor ${floor.floor_number}`;
  };

  const isDisableOrDeleteAction = ['disable', 'delete'].includes(confirmDialogState?.action);
  const isActionBlockedByBookings =
    isDisableOrDeleteAction && Boolean(confirmDialogState?.floor?.has_active_bookings);
  const bookingBlockMessage =
    confirmDialogState?.floor?.active_booking_alert ||
    'Active bookings exist in Confirmed/Check-In/Partial Check-In/Partial Check-Out status.';

  return (
    <Box sx={getConfigurationEmbeddedRootSx(isMobileLayout)}>
      {/* Header */}
      <Box sx={getConfigurationActionHeaderRowSx(isMobileLayout)}>
        <Typography
          variant="h5"
          sx={{
            ...getConfigurationActionHeaderTitleSx(isMobileLayout),
            fontWeight: 700,
            color: isDark ? '#fff' : '#212121',
          }}
        >
          Floors
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDrawer()}
          sx={getConfigurationAddButtonSx('#4ce1b6', isMobileLayout)}
        >
          Add Floor
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Floors Grid */}
      {!loading && (
        <Grid container spacing={getConfigurationCardGridSpacing(isMobileLayout)}>
          {floors.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 2 }}>
                No floors found. Click "Add Floor" to create your first floor.
              </Alert>
            </Grid>
          ) : (
            floors.map((floor) => (
              <Grid item xs={12} sm={6} md={3} key={floor.id}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: floor.is_active ? 1 : 0.72,
                    filter: floor.is_active ? 'none' : 'grayscale(0.2)',
                    '&:hover': {
                      elevation: 4,
                      transform: floor.is_active ? 'translateY(-4px)' : 'none',
                      boxShadow: floor.is_active
                        ? (isDark
                            ? '0 8px 24px rgba(76, 225, 182, 0.3)'
                            : '0 8px 24px rgba(25, 118, 210, 0.2)')
                        : 'none',
                      borderColor: isDark ? '#4ce1b6' : '#4ce1b6',
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    {/* Header with Edit and Delete Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LayersIcon 
                            sx={{ 
                              fontSize: 24, 
                              color: '#4ce1b6',
                            }} 
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: isDark ? '#fff' : '#212121',
                            }}
                          >
                            {getFloorDisplayName(floor)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <HotelIcon sx={{ fontSize: 16, color: isDark ? '#999' : '#757575' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: isDark ? '#999' : '#757575',
                              fontWeight: 600,
                            }}
                          >
                            {getRoomCountText(floor.room_count || 0)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDrawer(floor)}
                          disabled={deletingId === floor.id || togglingId === floor.id}
                          sx={{
                            color: isDark ? '#4ce1b6' : '#1976d2',
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip
                          title={
                            floor.room_count > 0
                              ? `Cannot delete floor with ${floor.room_count} room(s) attached`
                              : floor.has_active_bookings
                                ? 'Active bookings found. Open to view details'
                              : 'Delete floor'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(floor)}
                              disabled={deletingId === floor.id || togglingId === floor.id || (floor.room_count && floor.room_count > 0)}
                              sx={{
                                color: isDark ? '#ff5252' : '#d32f2f',
                                '&:hover': {
                                  backgroundColor: isDark ? 'rgba(255, 82, 82, 0.1)' : 'rgba(211, 47, 47, 0.08)',
                                },
                                '&.Mui-disabled': {
                                  color: isDark ? 'rgba(255, 82, 82, 0.3)' : 'rgba(211, 47, 47, 0.3)',
                                },
                              }}
                            >
                              {deletingId === floor.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0' }} />

                    {/* Description */}
                    {floor.description && (
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? '#ddd' : '#666',
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {floor.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Status Section with Toggle */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 2 }}>
                      <Chip
                        size="small"
                        label={floor.is_active ? 'Enabled' : 'Disabled'}
                        sx={{
                          height: 24,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          backgroundColor: floor.is_active
                            ? (isDark ? 'rgba(76, 225, 182, 0.18)' : 'rgba(76, 225, 182, 0.14)')
                            : (isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.14)'),
                          color: floor.is_active ? '#4ce1b6' : (isDark ? '#ffb74d' : '#ef6c00'),
                        }}
                      />
                      <Tooltip title={floor.is_active ? 'Disable floor' : 'Enable floor'}>
                        <span>
                          <IconButton
                            onClick={() => handleToggleServiceable(floor)}
                            disabled={togglingId === floor.id || deletingId === floor.id}
                            sx={{
                              p: 0,
                              color: floor.is_active
                                ? (isDark ? '#ffb74d' : '#ef6c00')
                                : '#4ce1b6',
                              '&:hover': {
                                backgroundColor: floor.is_active
                                  ? (isDark ? 'rgba(255, 183, 77, 0.12)' : 'rgba(239, 108, 0, 0.1)')
                                  : (isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.1)'),
                              },
                            }}
                          >
                            {togglingId === floor.id ? (
                              <CircularProgress size={30} />
                            ) : floor.is_active ? (
                              <ToggleOnIcon sx={{ fontSize: 40 }} />
                            ) : (
                              <ToggleOffIcon sx={{ fontSize: 40 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Add/Edit Floor Drawer — layout matches Room Type drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LayersIcon sx={{ color: '#4ce1b6', fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                {editingId ? 'Edit Floor' : 'Add New Floor'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onClick={handleCloseDrawer}
              disabled={saving}
              sx={{
                borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                color: '#4ce1b6',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.3,
                minWidth: 84,
                '&:hover': {
                  borderColor: '#4ce1b6',
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                },
                '&.Mui-disabled': {
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#d0d0d0',
                  color: isDark ? '#666' : '#999',
                },
              }}
            >
              Close
            </Button>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
              <TextField
                label="Floor Number *"
                type="number"
                value={formData.floor_number}
                onChange={(e) => handleInputChange('floor_number', e.target.value)}
                fullWidth
                variant="outlined"
                required
                error={!!errors.floor_number}
                helperText={errors.floor_number || 'Enter floor number (negative for basement, e.g., -1, 0 for ground, 1, 2, etc.)'}
                inputProps={{ min: -10, max: 200 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

              <TextField
                label="Floor Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                fullWidth
                variant="outlined"
                error={!!errors.name}
                helperText={errors.name || 'Optional: Custom name for the floor (e.g., "Lobby", "Penthouse")'}
                placeholder="e.g., Ground, Lobby, Penthouse"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Optional description or notes about the floor..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                      },
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4ce1b6',
                        borderWidth: '2px',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#4ce1b6',
                  },
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#4ce1b6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#4ce1b6',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDark ? '#ddd' : '#666',
                      fontWeight: 500,
                    }}
                  >
                    Serviceable Floor
                  </Typography>
                }
              />
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 1.5,
              borderTop: `2px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
              backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            }}
          >
            <Box sx={getConfigurationDrawerFooterSx(isMobileLayout)}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleCloseDrawer}
                disabled={saving}
                sx={{
                  borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                  color: isDark ? '#ddd' : '#666',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#4ce1b6',
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                    color: '#4ce1b6',
                  },
                  '&:disabled': {
                    borderColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#666' : '#999',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSaveFloor}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  backgroundColor: '#4ce1b6',
                  color: '#000',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#4ce1b6',
                    opacity: 0.9,
                    boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
                    color: isDark ? '#999' : '#666',
                  },
                }}
              >
                {editingId ? 'Update Floor' : 'Create Floor'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmCancel}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            fontWeight: 600,
            pb: 2,
          }}
        >
          {confirmDialogState?.action === 'delete'
            ? 'Delete Floor'
            : confirmDialogState?.action === 'disable'
              ? 'Disable Floor'
              : 'Enable Floor'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#666',
              fontSize: '1rem',
            }}
          >
            {confirmDialogState?.action === 'delete' ? (
              <>
                To delete floor <strong>"{getFloorDisplayName(confirmDialogState?.floor || {})}"</strong>, type <strong>confirm</strong> below.
              </>
            ) : confirmDialogState?.action === 'disable' ? (
              <>
                To disable floor <strong>"{getFloorDisplayName(confirmDialogState?.floor || {})}"</strong>, type <strong>confirm</strong> below.
              </>
            ) : (
              <>
                To enable floor <strong>"{getFloorDisplayName(confirmDialogState?.floor || {})}"</strong>, type <strong>confirm</strong> below.
              </>
            )}
          </DialogContentText>
          {isActionBlockedByBookings && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {bookingBlockMessage}
            </Alert>
          )}
          {confirmDialogState?.action === 'enable' && (
            <DialogContentText
              sx={{
                mt: 1,
                color: isDark ? '#ffcc80' : '#ef6c00',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              Default channel allocation will be set to full room count for affected room types. You can adjust channel allocation manually from Distribution.
            </DialogContentText>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label='Type "confirm"'
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button
            onClick={handleConfirmCancel}
            variant="outlined"
            sx={{
              color: '#4ce1b6',
              borderColor: '#4ce1b6',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1.5,
              px: 2,
              '&:hover': {
                borderColor: '#4ce1b6',
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={
              confirmDialogState?.action === 'delete'
                ? 'error'
                : confirmDialogState?.action === 'disable'
                  ? 'warning'
                  : 'success'
            }
            disabled={
              (deletingId !== null || togglingId !== null) ||
              confirmInput.trim().toLowerCase() !== 'confirm' ||
              isActionBlockedByBookings
            }
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deletingId !== null || togglingId !== null ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Processing...
              </>
            ) : confirmDialogState?.action === 'delete' ? (
              'Delete'
            ) : confirmDialogState?.action === 'disable' ? (
              'Disable'
            ) : (
              'Enable'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            color: isDark ? '#fff' : '#212121',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorPage;

